import { useEffect, useState } from 'react'
import { useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import {
  CLOSE_AUTH_MISMATCH,
  ConsoleSocket,
  consoleWsUrl,
  type ChangeMessage,
  type LiveStatus,
  type ServerMessage,
} from '../api/ws'
import { ApiError } from '../api/client'
import type {
  AccessEvent,
  AccessGroup,
  AccessSystem,
  CommandLog,
  Credential,
  Door,
  DoorCode,
  DoorCodeSeries,
  DoorPolicy,
  EventsParams,
  Operator,
  Page,
  Person,
  Schedule,
  SyncRun,
  WebhookSubscription,
  Zone,
} from '../api/types'
import { msUntilCustomerTokenExpiry, useAuthStore } from '../stores/auth'
import { isExpired } from '../lib/jwt'

/** Upsert into a Page: replace by unique_id when present, otherwise prepend. */
function upsertPage<T extends { unique_id: string }>(data: Page<T> | undefined, item: T): Page<T> | undefined {
  if (!data) return data
  const idx = data.items.findIndex((i) => i.unique_id === item.unique_id)
  const items =
    idx >= 0 ? data.items.map((i) => (i.unique_id === item.unique_id ? item : i)) : [item, ...data.items]
  return { ...data, items }
}

/**
 * Coalesce burst invalidations per query key: the first call fires
 * immediately, further calls inside the window collapse into ONE trailing
 * refetch. A bulk sync or batch import publishes hundreds of change
 * messages in seconds — without this each one refetches the location tree
 * (and friends), and the refetch storm slows the very batch that triggered
 * it. `dispose()` clears pending trailing timers on unmount/scope switch.
 */
export class InvalidationCoalescer {
  private lastRun = new Map<string, number>()
  private timers = new Map<string, ReturnType<typeof setTimeout>>()

  constructor(
    private queryClient: QueryClient,
    private windowMs = 750,
  ) {}

  fire(key: readonly unknown[], predicate?: (query: { queryKey: readonly unknown[] }) => boolean): void {
    const id = JSON.stringify(key) + (predicate ? ':pred' : '')
    const now = Date.now()
    const last = this.lastRun.get(id) ?? Number.NEGATIVE_INFINITY
    const run = () => {
      this.lastRun.set(id, Date.now())
      void this.queryClient.invalidateQueries({
        queryKey: key,
        predicate: predicate as never,
      })
    }
    if (now - last >= this.windowMs) {
      run()
      return
    }
    if (!this.timers.has(id)) {
      this.timers.set(
        id,
        setTimeout(() => {
          this.timers.delete(id)
          run()
        }, this.windowMs - (now - last)),
      )
    }
  }

  dispose(): void {
    for (const timer of this.timers.values()) clearTimeout(timer)
    this.timers.clear()
  }
}

/** Upsert by unique_id into an array cache; remove the item on `deleted`. */
function upsertArray<T extends { unique_id: string }>(
  data: T[] | undefined,
  item: T,
  deleted: boolean,
): T[] | undefined {
  if (!data) return data
  const idx = data.findIndex((i) => i.unique_id === item.unique_id)
  if (deleted) return idx >= 0 ? data.filter((i) => i.unique_id !== item.unique_id) : data
  if (idx >= 0) return data.map((i) => (i.unique_id === item.unique_id ? item : i))
  return [item, ...data]
}

/** Prefix of every location Credentials card query (`useLocationCredentialUsage`). */
const CREDENTIAL_USAGE_KEY = (scope: string) => [scope, 'locations', 'credential-usage'] as const

/** Exported for tests. */
export function applyChange(queryClient: QueryClient, scope: string, msg: ChangeMessage, coalesce: InvalidationCoalescer): void {
  switch (msg.kind) {
    case 'event': {
      if (msg.action !== 'created') return
      const event = msg.object
      // Prepend to the first page of every infinite events query whose
      // filters the event matches; leave other pages (and cursors) intact.
      for (const query of queryClient.getQueryCache().findAll({ queryKey: [scope, 'events', 'infinite'] })) {
        const params = (query.queryKey[3] ?? {}) as EventsParams
        if (params.type && params.type !== event.type) continue
        if (params.severity && params.severity !== event.severity) continue
        queryClient.setQueryData<InfiniteData<Page<AccessEvent>>>(query.queryKey, (data) =>
          data && data.pages.length > 0
            ? {
                ...data,
                pages: [{ ...data.pages[0], items: [event, ...data.pages[0].items] }, ...data.pages.slice(1)],
              }
            : data,
        )
      }
      // Overview shows recent events + 24h counts via useEvents — coalesced:
      // a sync burst prepends every event surgically above but must not
      // refetch the overview queries per message (infinite queries are
      // skipped, they were just patched).
      coalesce.fire([scope, 'events'], (query) => query.queryKey[2] !== 'infinite')
      // A granted access moves its credential's `last_used_*` (stamped by
      // ingest with a plain UPDATE, so no `credential` change message
      // follows) — refresh the last-used views, coalesced like the above.
      if (event.type === 'access.granted' && event.credential) {
        coalesce.fire([scope, 'credentials'])
        coalesce.fire(CREDENTIAL_USAGE_KEY(scope))
      }
      return
    }
    case 'command': {
      // The Unlock page's result panel reads this query — it updates live.
      queryClient.setQueryData<Page<CommandLog>>([scope, 'commands'], (data) => upsertPage(data, msg.object))
      return
    }
    case 'system': {
      const system = msg.object
      const deleted = msg.action === 'deleted'
      queryClient.setQueryData<AccessSystem[]>([scope, 'systems'], (data) => {
        if (!data) return data
        return upsertArray(data, system, deleted)
      })
      // Reconcile also advances location-binding state — refetch that system's list.
      coalesce.fire([scope, 'location-bindings', system.unique_id])
      return
    }
    case 'sync_run': {
      const run = msg.object
      // Keys are [scope, 'sync-runs', system ?? ''] — respect the system filter.
      for (const query of queryClient.getQueryCache().findAll({ queryKey: [scope, 'sync-runs'] })) {
        const filter = (query.queryKey[2] ?? '') as string
        if (filter !== '' && run.system !== filter) continue
        queryClient.setQueryData<Page<SyncRun>>(query.queryKey, (data) => upsertPage(data, run))
      }
      return
    }
    case 'unknown_credential': {
      // The inbox queries now hold per-CODE groups (deduped across systems);
      // the message is a single sighting — a surgical patch can't rebuild the
      // group, so invalidate (coalesced) instead. Low-volume kind.
      coalesce.fire([scope, 'unknown-credentials'])
      return
    }
    case 'person': {
      const person = msg.object
      const deleted = msg.action === 'deleted'
      // Surgical only on the unfiltered list: a rename can move a person in
      // or out of a search-filtered list, and reimplementing the server-side
      // search here is fragile — so filtered lists and the detail blob (with
      // its occupancy/sync data) are invalidated instead.
      queryClient.setQueryData<Person[]>([scope, 'persons', ''], (data) => upsertArray(data, person, deleted))
      coalesce.fire([scope, 'persons'], (query) => query.queryKey[2] !== '')
      // Access-group member rows show display_name/status.
      coalesce.fire([scope, 'access-groups'], (query) => query.queryKey[3] === 'members')
      // Location Credentials cards show person names and occupants (#56).
      coalesce.fire(CREDENTIAL_USAGE_KEY(scope))
      return
    }
    case 'occupancy': {
      // No standalone occupancy list in the console — occupancies surface as
      // nested blobs on the person and location details.
      coalesce.fire([scope, 'persons', 'detail', msg.object.person])
      coalesce.fire([scope, 'locations', 'detail', msg.object.location])
      // Occupants decide which credentials a location's card lists (#56).
      coalesce.fire(CREDENTIAL_USAGE_KEY(scope))
      return
    }
    case 'location': {
      // Create/move/rename/delete are structural — invalidate the tree rather
      // than splicing LocationNode.children client-side, plus the detail blob.
      // Coalesced: a split-on or sync creates hundreds of nodes in seconds.
      coalesce.fire([scope, 'locations', 'tree'])
      coalesce.fire([scope, 'locations', 'detail', msg.object.unique_id])
      return
    }
    case 'door': {
      queryClient.setQueryData<Door[]>([scope, 'doors'], (data) =>
        upsertArray(data, msg.object, msg.action === 'deleted'),
      )
      return
    }
    case 'zone': {
      queryClient.setQueryData<Zone[]>([scope, 'zones'], (data) =>
        upsertArray(data, msg.object, msg.action === 'deleted'),
      )
      return
    }
    case 'credential': {
      const cred = msg.object
      const deleted = msg.action === 'deleted'
      // Surgical only on the unfiltered list (exact 2-part key): a change
      // can move a row's position under an `order`, so the ordered variants
      // are invalidated instead (mirrors the door_code pattern below).
      queryClient.setQueryData<Credential[]>([scope, 'credentials'], (data) => upsertArray(data, cred, deleted))
      // (not the detail -- it's patched just below)
      coalesce.fire([scope, 'credentials'], (query) => query.queryKey.length > 2 && query.queryKey[2] !== 'detail')
      // Location Credentials cards show holder name, status and last use (#56).
      coalesce.fire(CREDENTIAL_USAGE_KEY(scope))
      // The detail is the same Credential shape — patch it when cached (never
      // create an entry for a screen nobody is watching); invalidate on delete.
      if (deleted) {
        queryClient.invalidateQueries({ queryKey: [scope, 'credentials', 'detail', cred.unique_id] })
      } else {
        queryClient.setQueryData<Credential>([scope, 'credentials', 'detail', cred.unique_id], (old) =>
          old ? cred : old,
        )
      }
      return
    }
    case 'access_group': {
      const group = msg.object
      const deleted = msg.action === 'deleted'
      // Exact key — member lists live at [scope, 'access-groups', uid, 'members'].
      queryClient.setQueryData<AccessGroup[]>([scope, 'access-groups'], (data) => upsertArray(data, group, deleted))
      if (deleted) queryClient.invalidateQueries({ queryKey: [scope, 'access-groups', group.unique_id, 'members'] })
      return
    }
    case 'schedule': {
      queryClient.setQueryData<Schedule[]>([scope, 'schedules'], (data) =>
        upsertArray(data, msg.object, msg.action === 'deleted'),
      )
      // Kind-filtered list queries ([scope, 'schedules', kind]) can't be upserted
      // kind-safely — refetch them instead (coalesced against sync bursts).
      coalesce.fire([scope, 'schedules'], (q) => q.queryKey.length > 2)
      return
    }
    case 'member_rule': {
      // A rule change re-derives the group's membership.
      queryClient.invalidateQueries({ queryKey: [scope, 'access-groups', msg.object.group, 'members'] })
      return
    }
    case 'door_policy': {
      queryClient.setQueryData<DoorPolicy[]>([scope, 'door-policies'], (data) =>
        upsertArray(data, msg.object, msg.action === 'deleted'),
      )
      return
    }
    case 'door_code': {
      // Surgical only on the unfiltered list (exact 2-part key): a change
      // can move a code in or out of a series filter, so the filtered
      // variants (series=none / a series uid) are invalidated instead.
      queryClient.setQueryData<DoorCode[]>([scope, 'door-codes'], (data) =>
        upsertArray(data, msg.object, msg.action === 'deleted'),
      )
      coalesce.fire([scope, 'door-codes'], (query) => query.queryKey.length > 2)
      return
    }
    case 'door_code_series': {
      queryClient.setQueryData<DoorCodeSeries[]>([scope, 'door-code-series'], (data) =>
        upsertArray(data, msg.object, msg.action === 'deleted'),
      )
      // A roll creates/retires period codes — every door-code list follows
      // (the unfiltered list included: period codes come and go there too).
      coalesce.fire([scope, 'door-codes'])
      return
    }
    case 'intercom_contact': {
      const contact = msg.object
      // Contacts surface as nested blobs on person and location details.
      if (contact.person) coalesce.fire([scope, 'persons', 'detail', contact.person])
      coalesce.fire([scope, 'locations', 'detail', contact.location])
      return
    }
    case 'webhook': {
      // No console screen reads webhooks yet — upsert anyway so a future
      // [scope, 'webhooks'] query stays live for free (no-op while uncached).
      queryClient.setQueryData<WebhookSubscription[]>([scope, 'webhooks'], (data) =>
        upsertArray(data, msg.object, msg.action === 'deleted'),
      )
      return
    }
    case 'operator': {
      queryClient.setQueryData<Operator[]>([scope, 'operators'], (data) =>
        upsertArray(data, msg.object, msg.action === 'deleted'),
      )
      return
    }
  }
}

/** Renew this far ahead of the customer token's exp. */
const RENEW_AHEAD_MS = 60_000

/** Staff diagnostics tap (the ops-tail drawer): every raw /ws frame,
 *  fan-out only — no cache side effects. The drawer subscribes ONLY while
 *  open, so for everyone else (and for non-staff always) this set is empty
 *  and the fan-out is a no-op. */
type RawFrameListener = (msg: ServerMessage) => void
const rawFrameListeners = new Set<RawFrameListener>()
export function subscribeRawFrames(fn: RawFrameListener): () => void {
  rawFrameListeners.add(fn)
  return () => {
    rawFrameListeners.delete(fn)
  }
}
/** Delay before retrying a renewal that failed transiently (network blip). */
const RENEW_RETRY_MS = 30_000

type RenewResult = 'ok' | 'transient' | 'fatal'

/**
 * Connect the console to the live /ws channel while a valid customer token
 * exists; disconnect on logout or customer switch. Change messages patch the
 * React Query caches directly (no refetch storm). Returns the connection
 * status for the live indicator.
 *
 * Token renewal chain: the customer token (15 min) is renewed proactively
 * ~60s before exp and pushed in-band as an `auth` frame; when that is missed
 * (sleep, suspended tab) a 4401 close or an expired token at a reconnect
 * attempt triggers renewal + reconnect instead. Only when renewal is
 * impossible (identity token expired) is the session expired.
 */
export function useConsoleLive(): LiveStatus {
  const queryClient = useQueryClient()
  // Not the token: a renewal swaps accessToken, and the socket must survive it.
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const [status, setStatus] = useState<LiveStatus>('disconnected')

  useEffect(() => {
    if (scope === 'none') {
      setStatus('disconnected')
      return
    }
    let disposed = false
    let renewTimer: ReturnType<typeof setTimeout> | null = null

    /** Always build the URL from the latest store token — never capture one. */
    const urlFor = () => consoleWsUrl(useAuthStore.getState().accessToken ?? '')

    function failSession(): void {
      useAuthStore.getState().expireSession()
      if (window.location.pathname !== '/login') window.location.assign('/login')
    }

    /** 'fatal' = identity token expired (exchange 401); anything else is retried. */
    async function attemptRenew(): Promise<RenewResult> {
      try {
        await useAuthStore.getState().renewCustomerToken()
        return 'ok'
      } catch (err) {
        return err instanceof ApiError && err.status === 401 ? 'fatal' : 'transient'
      }
    }

    function clearRenewTimer(): void {
      if (renewTimer !== null) {
        clearTimeout(renewTimer)
        renewTimer = null
      }
    }

    function scheduleRenewal(delayMs?: number): void {
      clearRenewTimer()
      let delay = delayMs
      if (delay === undefined) {
        const ms = msUntilCustomerTokenExpiry()
        if (ms === null) return
        delay = Math.max(ms - RENEW_AHEAD_MS, 0)
      }
      renewTimer = setTimeout(() => {
        renewTimer = null
        void onRenewTimer()
      }, delay)
    }

    /** Proactive renewal ~60s before exp; also the transient-retry entry point. */
    async function onRenewTimer(): Promise<void> {
      const result = await attemptRenew()
      if (disposed) return
      if (result === 'fatal') {
        failSession()
        return
      }
      if (result === 'transient') {
        scheduleRenewal(RENEW_RETRY_MS)
        return
      }
      scheduleRenewal()
      // Keep REST alive via the store; re-auth the socket too when it is up.
      // When the socket is down (missed renewal), reconnect with the fresh token.
      const token = useAuthStore.getState().accessToken
      if (!token) return
      if (socket.isStopped()) socket.connect(urlFor)
      else socket.sendAuth(token)
    }

    const coalescer = new InvalidationCoalescer(queryClient)
    const socket = new ConsoleSocket({
      onStatus: (s) => {
        if (!disposed) setStatus(s)
      },
      onAuthFailure: (code) => {
        if (code === CLOSE_AUTH_MISMATCH) {
          // Re-auth with another customer's token — should never happen; fatal.
          failSession()
          return
        }
        // 4401: the current token lapsed before we re-authed (laptop sleep,
        // suspended tab). Renew, then reconnect with the fresh token.
        void (async () => {
          const result = await attemptRenew()
          if (disposed) return
          if (result === 'ok') {
            scheduleRenewal()
            socket.connect(urlFor)
          } else if (result === 'transient') {
            scheduleRenewal(RENEW_RETRY_MS) // the retry timer reconnects on success
          } else {
            failSession()
          }
        })()
      },
      onBeforeConnect: async () => {
        // Reconnect attempt: valid token → proceed with the normal backoff.
        if (!isExpired(useAuthStore.getState().accessToken)) return true
        const result = await attemptRenew()
        if (result === 'ok') {
          scheduleRenewal()
          return true
        }
        if (result === 'transient') {
          scheduleRenewal(RENEW_RETRY_MS) // the retry timer reconnects on success
          return false
        }
        failSession()
        return false
      },
      onMessage: (msg) => {
        for (const fn of rawFrameListeners) fn(msg)
        if (msg.type === 'change') applyChange(queryClient, scope, msg, coalescer)
        else if (msg.type === 'auth_error') failSession() // server also closes 4403
      },
    })

    // Startup: a restored session may hold an expired customer token.
    void (async () => {
      if (isExpired(useAuthStore.getState().accessToken)) {
        const result = await attemptRenew()
        if (disposed) return
        if (result === 'fatal') {
          failSession()
          return
        }
        if (result === 'transient') {
          scheduleRenewal(RENEW_RETRY_MS) // the retry timer connects on success
          return
        }
      }
      if (disposed) return
      scheduleRenewal()
      socket.connect(urlFor)
    })()

    return () => {
      disposed = true
      clearRenewTimer()
      coalescer.dispose()
      socket.close()
    }
  }, [scope, queryClient])

  return status
}
